import React, { useEffect, useMemo, useState } from "react";
import MuxPlayer from "@mux/mux-player-react";
import { apiUrl, getApiBase } from "../lib/apiBase";
import { resolveBadgeImage } from "../lib/badges";

// Social Feed backed by posts.json + post_comments.json
export default function Feed() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const deepLink = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return {
        postId: String(params.get("post") || "").trim(),
        commentId: String(params.get("comment") || "").trim(),
      };
    } catch {
      return { postId: "", commentId: "" };
    }
  }, []);

  const [token, setToken] = useState(() => {
    try {
      return (
        localStorage.getItem("ga_token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("token") ||
        ""
      );
    } catch {
      return "";
    }
  });

  useEffect(() => {
    const refresh = () => {
      try {
        setToken(
          localStorage.getItem("ga_token") ||
            localStorage.getItem("authToken") ||
            localStorage.getItem("token") ||
            ""
        );
      } catch {
        setToken("");
      }
    };
    window.addEventListener("auth:changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("auth:changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  async function load() {
    setErr("");
    setLoading(true);
    try {
      // apiUrl() already prefixes /api, so do not pass /api/... here.
      const r = await fetch(apiUrl("/posts"), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 401) {
        try {
          ["ga_token", "token", "authToken", "jwt", "streamsite_token"].forEach((k) =>
            localStorage.removeItem(k)
          );
          window.dispatchEvent(new Event("auth:changed"));
        } catch {}
        throw new Error("Session expired. Please sign in again.");
      }
      if (!r.ok) throw new Error(data?.error || "failed");
      setPosts(Array.isArray(data?.posts) ? data.posts : []);
    } catch (e) {
      setErr(String(e?.message || e));
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!posts.length) return undefined;
    let targetId = deepLink.postId ? `post-${deepLink.postId}` : "";
    if (!targetId && window.location.hash.startsWith("#post-")) {
      targetId = window.location.hash.slice(1);
      try {
        targetId = decodeURIComponent(targetId);
      } catch {}
    }
    if (!targetId) return undefined;

    const timer = window.setTimeout(() => {
      document.getElementById(targetId)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);

    return () => window.clearTimeout(timer);
  }, [posts, deepLink.postId]);

  return (
    <div
      style={{
        maxWidth: 760,
        margin: "0 auto",
        padding: "22px 14px 80px",
      }}
    >
      <div
        style={{
          background: "rgba(0,0,0,0.55)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 18,
          padding: "16px 18px",
          backdropFilter: "blur(10px)",
          boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: 0.2 }}>
              Feed
            </div>
            <div style={{ opacity: 0.8, fontSize: 13 }}>
              Latest gameplay uploads, clips, likes, and comments from the community.
            </div>
          </div>

          <button
            type="button"
            onClick={load}
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.16)",
              background: "rgba(255,255,255,0.06)",
              color: "white",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            Refresh
          </button>
        </div>

        {err ? (
          <div
            style={{
              marginTop: 12,
              padding: 10,
              borderRadius: 12,
              background: "rgba(255,0,0,0.12)",
              border: "1px solid rgba(255,0,0,0.25)",
              color: "white",
              fontSize: 13,
            }}
          >
            {err}
          </div>
        ) : null}
      </div>

      {loading ? (
        <div style={{ opacity: 0.85 }}>Loading feed…</div>
      ) : posts.length === 0 ? (
        <div style={{ opacity: 0.85 }}>
          No uploads yet. Go to your profile and upload gameplay — it will show here
          automatically.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 14 }}>
          {posts.map((p) => (
            <PostCard key={p.id} post={p} token={token} targetPostId={deepLink.postId} targetCommentId={deepLink.commentId} />
          ))}
        </div>
      )}
    </div>
  );
}

function clearExpiredAuth() {
  try {
    ["ga_token", "token", "authToken", "jwt", "streamsite_token"].forEach((key) =>
      localStorage.removeItem(key)
    );
    window.dispatchEvent(new Event("auth:changed"));
  } catch {}
}

function PostCard({ post, token, targetPostId = "", targetCommentId = "" }) {
  const isDeepLinkedPost = String(targetPostId) === String(post?.id);
  const [openComments, setOpenComments] = useState(Boolean(isDeepLinkedPost && targetCommentId));
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentErr, setCommentErr] = useState("");
  const [commentCount, setCommentCount] = useState(
    Math.max(0, Number(post?.commentCount || 0))
  );

  const [likeCount, setLikeCount] = useState(Math.max(0, Number(post?.likeCount || 0)));
  const [likedByMe, setLikedByMe] = useState(Boolean(post?.likedByMe));
  const [lastLikedBy, setLastLikedBy] = useState(post?.lastLikedBy || null);
  const [likeBusy, setLikeBusy] = useState(false);
  const [socialMessage, setSocialMessage] = useState("");

  useEffect(() => {
    setCommentCount(Math.max(0, Number(post?.commentCount || 0)));
    setLikeCount(Math.max(0, Number(post?.likeCount || 0)));
    setLikedByMe(Boolean(post?.likedByMe));
    setLastLikedBy(post?.lastLikedBy || null);
  }, [post?.commentCount, post?.likeCount, post?.likedByMe, post?.lastLikedBy]);

  const createdLabel = useMemo(() => {
    const ts = Number(post?.createdAt || 0);
    if (!ts) return "";
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return "";
    }
  }, [post?.createdAt]);

  const isClip = Boolean(post?.isClip || post?.clip);
  const isWallPost = String(post?.postType || post?.type || "") === "profile_wall";

  const muxClipProps = useMemo(() => {
    if (!isClip) return {};

    const offsetStart = Number(post?.clipStartOffsetSeconds || 0);
    const offsetEnd = Number(post?.clipEndOffsetSeconds || 0);
    if (offsetEnd > offsetStart) {
      return { startTime: offsetStart };
    }

    const start = Number(post?.programStartTime || 0);
    const end = Number(post?.programEndTime || 0);
    if (!start || !end || end <= start) return {};
    return {
      programStartTime: start,
      programEndTime: end,
    };
  }, [
    isClip,
    post?.clipStartOffsetSeconds,
    post?.clipEndOffsetSeconds,
    post?.programStartTime,
    post?.programEndTime,
  ]);

  const clipIsWaiting = Boolean(isClip && (post?.clipPending || !post?.playbackId));

  async function loadComments() {
    setCommentErr("");
    setCommentsLoading(true);
    try {
      const r = await fetch(apiUrl(`/posts/${post.id}/comments`));
      const data = await r.json().catch(() => ({}));
      if (r.status === 401) {
        clearExpiredAuth();
        throw new Error("Session expired. Please sign in again.");
      }
      if (!r.ok) throw new Error(data?.error || "failed");
      const nextComments = Array.isArray(data?.comments) ? data.comments : [];
      setComments(nextComments);
      setCommentCount(nextComments.length);
    } catch (e) {
      setCommentErr(String(e?.message || e));
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  }

  async function addComment() {
    if (!token) {
      setCommentErr("Please sign in to comment.");
      return;
    }
    const text = String(commentText || "").trim();
    if (!text) return;

    setCommentErr("");
    try {
      const r = await fetch(apiUrl(`/posts/${post.id}/comments`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 401) {
        clearExpiredAuth();
        throw new Error("Session expired. Please sign in again.");
      }
      if (!r.ok) throw new Error(data?.error || "failed");
      setCommentText("");
      await loadComments();
    } catch (e) {
      setCommentErr(String(e?.message || e));
    }
  }

  async function toggleLike() {
    if (!token) {
      setSocialMessage(isWallPost ? "Sign in to like posts." : "Sign in to like videos.");
      return;
    }
    if (likeBusy) return;

    setLikeBusy(true);
    setSocialMessage("");
    try {
      const r = await fetch(apiUrl(`/posts/${post.id}/like`), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 401) {
        clearExpiredAuth();
        throw new Error("Session expired. Please sign in again.");
      }
      if (!r.ok) throw new Error(data?.message || data?.error || "Could not update like.");

      setLikeCount(Math.max(0, Number(data?.likeCount || 0)));
      setLikedByMe(Boolean(data?.likedByMe));
      setLastLikedBy(data?.lastLikedBy || null);
    } catch (e) {
      setSocialMessage(String(e?.message || e));
    } finally {
      setLikeBusy(false);
    }
  }

  async function copyShareLink(shareUrl) {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareUrl);
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = shareUrl;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }

  async function sharePost() {
    const shareUrl = `${window.location.origin}/feed#post-${encodeURIComponent(post.id)}`;
    const title = String(
      isWallPost ? "GAStreaming profile post" : post?.caption || (isClip ? "GAStreaming clip" : "GAStreaming video")
    );
    const text = isWallPost
      ? `Check out this profile post on GAStreaming.live.`
      : `Check out ${title} on GAStreaming.live.`;

    setSocialMessage("");
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url: shareUrl });
        return;
      }

      await copyShareLink(shareUrl);
      setSocialMessage("Video link copied.");
      window.setTimeout(() => setSocialMessage(""), 2400);
    } catch (e) {
      if (e?.name === "AbortError") return;
      try {
        await copyShareLink(shareUrl);
        setSocialMessage("Video link copied.");
        window.setTimeout(() => setSocialMessage(""), 2400);
      } catch {
        setSocialMessage("Could not share this video.");
      }
    }
  }

  useEffect(() => {
    if (openComments) loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openComments]);

  useEffect(() => {
    if (!isDeepLinkedPost || !targetCommentId || commentsLoading || !comments.length) return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById(`comment-${targetCommentId}`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [comments, commentsLoading, isDeepLinkedPost, targetCommentId]);

  const lastLikeUsername = String(lastLikedBy?.username || "").trim();
  const lastLikeDisplayName = String(
    lastLikedBy?.displayName || lastLikedBy?.username || "member"
  ).trim();
  const lastLikeLabel = lastLikeUsername ? `@${lastLikeUsername}` : lastLikeDisplayName;

  return (
    <article
      id={`post-${post.id}`}
      style={{
        background: "rgba(0,0,0,0.55)",
        border: isDeepLinkedPost
          ? "1px solid rgba(255,23,68,0.72)"
          : "1px solid rgba(255,255,255,0.12)",
        borderRadius: 18,
        overflow: "hidden",
        backdropFilter: "blur(10px)",
        boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
        scrollMarginTop: 190,
      }}
    >
      <div
        style={{
          padding: "14px 14px 10px",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              minWidth: 0,
            }}
          >
            {isClip ? (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing: 0.6,
                  color: "#fde68a",
                  border: "1px solid rgba(253,230,138,0.35)",
                  background: "rgba(180,83,9,0.22)",
                  borderRadius: 999,
                  padding: "3px 7px",
                  whiteSpace: "nowrap",
                }}
              >
                CLIP
              </span>
            ) : null}
            <div
              style={{
                fontWeight: 800,
                letterSpacing: 0.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {isWallPost
                ? "Profile Wall Post"
                : post?.caption || (isClip ? "Stream clip" : "Gameplay upload")}
            </div>
          </div>
          <div style={{ opacity: 0.75, fontSize: 12 }}>
            <a
              href={`/u/${encodeURIComponent(
                post?.username || post?.displayName || post?.name || "member"
              )}`}
              style={{ color: "inherit", fontWeight: 850 }}
            >
              @{post?.username || post?.displayName || post?.name || "member"}
            </a>
            {isWallPost && post?.targetUsername ? (
              <>
                {" → "}
                <a
                  href={`/u/${encodeURIComponent(post.targetUsername)}`}
                  style={{ color: "#ff8297", fontWeight: 850 }}
                >
                  @{post.targetUsername}
                </a>
              </>
            ) : null}{" "}
            {createdLabel ? `• ${createdLabel}` : ""}
          </div>
        </div>
      </div>

      <div style={{ padding: 12, paddingTop: 0 }}>
        {isWallPost ? (
          <div
            style={{
              padding: "18px 16px",
              borderRadius: 14,
              border: "1px solid rgba(255,255,255,0.12)",
              background:
                "linear-gradient(145deg, rgba(255,255,255,0.065), rgba(255,255,255,0.025))",
              fontSize: 16,
              lineHeight: 1.55,
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          >
            <div style={{ opacity: 0.58, fontSize: 11, fontWeight: 950, letterSpacing: 0.8, marginBottom: 8 }}>
              POSTED ON {String(post?.targetDisplayName || post?.targetUsername || "MEMBER").toUpperCase()}'S PROFILE
            </div>
            {post?.text || post?.caption || ""}
          </div>
        ) : post?.playbackId ? (
          <div
            style={{
              maxWidth: 680,
              margin: "0 auto",
              borderRadius: 14,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(0,0,0,0.45)",
            }}
          >
            {isClip ? (
              <ClipMuxPlayer post={post} muxClipProps={muxClipProps} />
            ) : (
              <MuxPlayer
                streamType={post?.streamType === "live" ? "live" : "on-demand"}
                playbackId={String(post.playbackId || "").trim()}
                metadata={{
                  video_id: String(post.id),
                  video_title: post?.caption || "Gameplay upload",
                  viewer_user_id: "public",
                }}
                style={{
                  width: "100%",
                  maxHeight: 390,
                  aspectRatio: "16/9",
                  display: "block",
                }}
              />
            )}
          </div>
        ) : clipIsWaiting ? (
          <div
            style={{
              minHeight: 150,
              display: "grid",
              placeItems: "center",
              textAlign: "center",
              borderRadius: 14,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(0,0,0,0.45)",
              padding: 18,
            }}
          >
            <div>
              <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 8 }}>
                Clip is processing
              </div>
              <div style={{ opacity: 0.82, fontSize: 13, lineHeight: 1.35 }}>
                This clip will play here after the live stream ends and Mux finishes the recording.
              </div>
            </div>
          </div>
        ) : (
          <div style={{ opacity: 0.8 }}>Missing playbackId for this post.</div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            marginTop: 12,
            paddingTop: 10,
            borderTop: "1px solid rgba(255,255,255,0.10)",
          }}
        >
          <button
            type="button"
            onClick={toggleLike}
            disabled={likeBusy}
            aria-pressed={likedByMe}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "9px 12px",
              borderRadius: 999,
              border: likedByMe
                ? "1px solid rgba(255,95,117,0.65)"
                : "1px solid rgba(255,255,255,0.16)",
              background: likedByMe
                ? "rgba(190,24,54,0.32)"
                : "rgba(255,255,255,0.05)",
              color: "white",
              cursor: likeBusy ? "wait" : "pointer",
              fontWeight: 900,
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>
              {likedByMe ? "♥" : "♡"}
            </span>
            <span>Like</span>
            <span style={{ opacity: 0.82 }}>{likeCount}</span>
          </button>

          <button
            type="button"
            onClick={() => setOpenComments((value) => !value)}
            aria-expanded={openComments}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "9px 12px",
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.16)",
              background: openComments
                ? "rgba(255,255,255,0.11)"
                : "rgba(255,255,255,0.05)",
              color: "white",
              cursor: "pointer",
              fontWeight: 900,
            }}
          >
            <span aria-hidden="true">💬</span>
            <span>Comments</span>
            <span style={{ opacity: 0.82 }}>{commentCount}</span>
            <span
              aria-hidden="true"
              style={{
                display: "inline-block",
                transform: openComments ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 160ms ease",
                opacity: 0.8,
              }}
            >
              ▾
            </span>
          </button>

          <button
            type="button"
            onClick={sharePost}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "9px 12px",
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.16)",
              background: "rgba(255,255,255,0.05)",
              color: "white",
              cursor: "pointer",
              fontWeight: 900,
            }}
          >
            <span aria-hidden="true">↗</span>
            <span>Share</span>
          </button>
        </div>

        {likeCount > 0 && lastLikedBy ? (
          <div style={{ marginTop: 9, fontSize: 13, opacity: 0.86 }}>
            Liked by <strong>{lastLikeLabel}</strong>
            {likeCount > 1
              ? ` and ${likeCount - 1} ${likeCount - 1 === 1 ? "other" : "others"}`
              : ""}
          </div>
        ) : null}

        {socialMessage ? (
          <div
            role="status"
            style={{
              marginTop: 9,
              fontSize: 13,
              padding: "8px 10px",
              borderRadius: 10,
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            {socialMessage}
          </div>
        ) : null}

        {openComments ? (
          <div
            style={{
              marginTop: 12,
              paddingTop: 12,
              borderTop: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
              <input
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && token && commentText.trim()) {
                    e.preventDefault();
                    addComment();
                  }
                }}
                placeholder={token ? "Add a comment…" : "Sign in to comment…"}
                disabled={!token}
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.16)",
                  background: "rgba(0,0,0,0.35)",
                  color: "white",
                  outline: "none",
                }}
              />
              <button
                type="button"
                onClick={addComment}
                disabled={!token || !commentText.trim()}
                style={{
                  padding: "10px 14px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.16)",
                  background:
                    !token || !commentText.trim()
                      ? "rgba(255,255,255,0.04)"
                      : "rgba(0,255,170,0.20)",
                  color: "white",
                  cursor: !token || !commentText.trim() ? "not-allowed" : "pointer",
                  fontWeight: 900,
                }}
              >
                Post
              </button>
            </div>

            {commentErr ? (
              <div
                style={{
                  marginBottom: 10,
                  padding: 10,
                  borderRadius: 12,
                  background: "rgba(255,0,0,0.12)",
                  border: "1px solid rgba(255,0,0,0.25)",
                  color: "white",
                  fontSize: 13,
                }}
              >
                {commentErr}
              </div>
            ) : null}

            {commentsLoading ? (
              <div style={{ opacity: 0.85 }}>Loading comments…</div>
            ) : comments.length === 0 ? (
              <div style={{ opacity: 0.85 }}>No comments yet.</div>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {comments.map((c) => (
                  <div
                    key={c.id}
                    id={`comment-${c.id}`}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 12,
                      scrollMarginTop: 170,
                      border:
                        String(targetCommentId) === String(c.id)
                          ? "1px solid rgba(255,23,68,0.78)"
                          : "1px solid rgba(255,255,255,0.12)",
                      background:
                        String(targetCommentId) === String(c.id)
                          ? "rgba(255,23,68,0.13)"
                          : "rgba(255,255,255,0.04)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
                      <CommentIdentity comment={c} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ opacity: 0.86, fontSize: 12, marginBottom: 4, fontWeight: 850 }}>
                          <a
                            href={`/u/${encodeURIComponent(c.username || c.displayName || c.name || "member")}`}
                            style={{ color: "inherit" }}
                          >
                            @{c.username || c.displayName || c.name || "member"}
                          </a>
                        </div>
                        <div style={{ fontSize: 14, lineHeight: 1.25 }}>{c.text}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </article>
  );
}



function CommentIdentity({ comment }) {
  const label = String(
    comment?.displayName || comment?.username || comment?.name || "member"
  ).trim();
  const badgeSrc = resolveBadgeImage(
    comment?.equippedBadgeKey,
    comment?.customBadgeUrl,
    getApiBase()
  );

  return (
    <span
      title={badgeSrc ? `${label}'s badge` : label}
      style={{
        width: 30,
        height: 30,
        minWidth: 30,
        borderRadius: 999,
        overflow: "hidden",
        display: "inline-grid",
        placeItems: "center",
        border: "1px solid rgba(255,255,255,0.18)",
        background: "linear-gradient(135deg, rgba(255,0,64,.26), rgba(255,255,255,.06))",
        boxShadow: "0 4px 14px rgba(0,0,0,.28)",
        fontSize: 12,
        fontWeight: 950,
      }}
    >
      {badgeSrc ? (
        <img src={badgeSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      ) : (
        (label[0] || "?").toUpperCase()
      )}
    </span>
  );
}

function ClipMuxPlayer({ post, muxClipProps }) {
  const playerRef = React.useRef(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const storedStart = Math.max(0, Number(post?.clipStartOffsetSeconds || 0));
  const rawEnd = Number(post?.clipEndOffsetSeconds || 0);
  const storedWindow = rawEnd > storedStart ? rawEnd - storedStart : 0;
  const legacyShortClip = rawEnd > 0 && storedWindow > 0 && storedWindow < 30;
  const start = legacyShortClip ? Math.max(0, rawEnd - 90) : storedStart;
  const configuredDuration = Math.max(1, legacyShortClip ? 90 : Number(post?.durationSeconds || 90));
  const availableDuration = rawEnd > start ? rawEnd - start : configuredDuration;
  const duration = Math.max(1, Math.round(Math.min(availableDuration, configuredDuration)));
  const end = start + duration;

  function getPlayer() {
    return playerRef.current;
  }

  function syncToClipWindow(forceStart = false) {
    const player = getPlayer();
    if (!player) return;

    const current = Number(player.currentTime || 0);
    if (forceStart || current < start || current >= end) {
      try {
        player.currentTime = start;
      } catch {}
      setElapsed(0);
      return;
    }

    setElapsed(Math.max(0, Math.min(duration, current - start)));
  }

  function togglePlay() {
    const player = getPlayer();
    if (!player) return;

    syncToClipWindow(Number(player.currentTime || 0) >= end);

    if (player.paused) {
      Promise.resolve(player.play()).catch(() => {});
    } else {
      player.pause();
    }
  }

  function replayClip() {
    const player = getPlayer();
    if (!player) return;
    try {
      player.currentTime = start;
    } catch {}
    setElapsed(0);
    Promise.resolve(player.play()).catch(() => {});
  }

  useEffect(() => {
    const player = getPlayer();
    if (!player) return undefined;

    const onLoaded = () => syncToClipWindow(true);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);
    const onTime = () => {
      const current = Number(player.currentTime || 0);
      if (current < start) {
        try {
          player.currentTime = start;
        } catch {}
        setElapsed(0);
        return;
      }
      if (current >= end) {
        try {
          player.pause();
          player.currentTime = start;
        } catch {}
        setPlaying(false);
        setElapsed(duration);
        return;
      }
      setElapsed(Math.max(0, Math.min(duration, current - start)));
    };

    player.addEventListener("loadedmetadata", onLoaded);
    player.addEventListener("play", onPlay);
    player.addEventListener("pause", onPause);
    player.addEventListener("ended", onEnded);
    player.addEventListener("timeupdate", onTime);

    return () => {
      player.removeEventListener("loadedmetadata", onLoaded);
      player.removeEventListener("play", onPlay);
      player.removeEventListener("pause", onPause);
      player.removeEventListener("ended", onEnded);
      player.removeEventListener("timeupdate", onTime);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.id, start, end, duration]);

  const progress = Math.max(0, Math.min(100, (elapsed / duration) * 100));

  return (
    <div
      className="ga-feed-clip-player"
      style={{
        position: "relative",
        background: "black",
        overflow: "hidden",
        isolation: "isolate",
      }}
    >
      <style>{`
        .ga-feed-clip-player mux-player {
          --controls: none;
          --media-control-display: none;
          --media-controls-display: none;
          --media-control-bar-display: none;
          --play-button-display: none;
          --seek-backward-button-display: none;
          --seek-forward-button-display: none;
          --mute-button-display: none;
          --captions-button-display: none;
          --airplay-button-display: none;
          --pip-button-display: none;
          --fullscreen-button-display: none;
          --time-display-display: none;
          --duration-display-display: none;
          --rendition-selectmenu-display: none;
          --playback-rate-button-display: none;
          --seek-live-button-display: none;
          --live-button-display: none;
        }
      `}</style>

      <MuxPlayer
        ref={playerRef}
        streamType="on-demand"
        playbackId={String(post.playbackId || "").trim()}
        {...muxClipProps}
        startTime={start}
        controls={false}
        nohotkeys
        metadata={{
          video_id: String(post.id),
          video_title: post?.caption || "Stream clip",
          viewer_user_id: "public",
        }}
        style={{
          width: "100%",
          maxHeight: 390,
          aspectRatio: "16/9",
          display: "block",
          pointerEvents: "none",
        }}
      />

      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 76,
          zIndex: 3,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.94) 34%, rgba(0,0,0,0.98))",
          pointerEvents: "none",
        }}
      />

      <button
        type="button"
        onClick={togglePlay}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 4,
          width: "100%",
          height: "100%",
          border: 0,
          background: "transparent",
          color: "white",
          cursor: "pointer",
          display: "grid",
          placeItems: "center",
          fontWeight: 900,
          fontSize: 44,
          textShadow: "0 4px 18px rgba(0,0,0,0.75)",
        }}
        aria-label={playing ? "Pause clip" : "Play clip"}
      >
        {playing ? "" : "▶"}
      </button>

      <div
        style={{
          position: "absolute",
          left: 12,
          right: 12,
          bottom: 10,
          zIndex: 6,
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 10px",
          borderRadius: 999,
          background: "rgba(0,0,0,0.88)",
          border: "1px solid rgba(255,255,255,0.16)",
          boxShadow: "0 10px 28px rgba(0,0,0,0.42)",
          pointerEvents: "auto",
        }}
      >
        <button
          type="button"
          onClick={togglePlay}
          style={{
            width: 34,
            height: 34,
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.18)",
            background: "rgba(255,255,255,0.10)",
            color: "white",
            cursor: "pointer",
            fontWeight: 900,
          }}
        >
          {playing ? "❚❚" : "▶"}
        </button>

        <div
          style={{
            flex: 1,
            height: 7,
            borderRadius: 999,
            overflow: "hidden",
            background: "rgba(255,255,255,0.18)",
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: "100%",
              borderRadius: 999,
              background: "linear-gradient(90deg, #22d3ee, #f472b6)",
            }}
          />
        </div>

        <div style={{ fontSize: 12, fontWeight: 900, color: "white", minWidth: 54 }}>
          {Math.floor(elapsed)} / {duration}s
        </div>

        <button
          type="button"
          onClick={replayClip}
          style={{
            width: 34,
            height: 34,
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.18)",
            background: "rgba(255,255,255,0.10)",
            color: "white",
            cursor: "pointer",
            fontWeight: 900,
          }}
          aria-label="Replay clip"
        >
          ↺
        </button>
      </div>
    </div>
  );
}
